import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {Button, Select, TextField} from "@vitalsource/vstui";
import {Search as SearchIcon} from "@vitalsource/vstui-icons";
import {SORT_ATTRIBUTE} from "../utils/constants";
import '../../styles/Layouts.css';
import { ViewMode } from './ProductsList';

export interface ToolbarProps {
  onUpdateFilter: (e: Event) => void;
  onUpdateSort: (e: Event) => void;
  onUpdateViewMode: (viewMode: ViewMode) => void;
  filter: string;
  sort: string;
  viewMode: ViewMode;
  btnAction: (e: MouseEvent) => void;
  addButtonRefCb: (ref: any) => void;
  emptyFilterState: boolean;
  hidden?: boolean;
}

const SearchField = styled(TextField)`
  position: relative;
  height: 40px;
  width: 522px;
  display: inline-block;
  > input[type="search"] {
    min-height: 0px;
    height: 100%;
    width: 100%;
  }
`;

const SearchIconOverLay = styled(SearchIcon)`
  position: relative;
  display: inline-block;
  height: 16px;
  width: 16px;
`;

const SelectDropDown = styled(Select)`
  position: relative;
  padding: auto 8px auto 8px;
  display: inline-block;
  width: 150px;
  > select {
    min-height: 0px;
    min-width: 0px;
    width: 150px;
    height: 40px;
    max-width: 100%;
  }
`;

const CustomButton = styled(Button)<{ disabledStyle: boolean }>`
  position: relative;
  width: 128px;
  height: 40px;
  font-weight: 700;
  min-height: 40px;
  > div {
    min-height: 0px;
    height: 100%;
  }
  background-color: ${({ disabledStyle }) => (disabledStyle ? "#ccc" : "rgb(0, 127, 173)")}; /* Styling disabled state */
  cursor: ${({ disabledStyle }) => (disabledStyle ? "not-allowed" : "pointer")}; /* Show disabled cursor */
  border: ${({ disabledStyle }) => (disabledStyle ? "none" : "1px solid rgb(0, 127, 173)")}; /* Remove border if disabled */
  opacity: ${({ disabledStyle }) => (disabledStyle ? "0.6" : "1")};
`;

const ViewToggleContainer = styled.div`
  display: flex;
  border-radius: 4px;
  border: 1px solid #ccc;
  overflow: hidden;
  position: absolute;
  right: -15rem;
  top: 25%;
  transform: translateY(-50%);
  align-items: center;
  justify-content: center;
  
  /* Small screens */
  @media screen and (max-width: 1535px) {
    right: -6rem;
  }

  /* Desktop screen */
  @media screen and (min-width: 1920px) {
    right: -27rem;
  }
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  background: ${({ active }) => active ? '#0BABE9' : 'transparent'};
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  color: ${({ active }) => active ? '#fff' : '#666'};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;

  &:hover {
    background: ${({ active }) => active ? '#0BABE9' : '#f5f5f5'};
  }

  i {
    font-size: 16px;
  }
`;

const ToolbarUtility = (props: ToolbarProps) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = (e: MouseEvent) => {
    if (props.filter.length > 0) {
      setShowTooltip(true);
    } else {
      props.btnAction(e);
    }
  };

  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showTooltip]);

  return (
    <div id="toolbar">
      <ViewToggleContainer>
        <ViewToggleButton
          active={props.viewMode === ViewMode.GRID}
          onClick={() => props.onUpdateViewMode(ViewMode.GRID)}
          title="Grid View"
        >
          <i className="fa fa-th" aria-hidden="true"></i>
        </ViewToggleButton>
        <ViewToggleButton
          active={props.viewMode === ViewMode.LIST}
          onClick={() => props.onUpdateViewMode(ViewMode.LIST)}
          title="List View"
        >
          <i className="fa fa-list-ul" aria-hidden="true"></i>
        </ViewToggleButton>
      </ViewToggleContainer>
      <div className={"toolbar"}>
        <div className={"toolbar__item toolbar__item--left icon-form"}>
          <SearchField
            className="product-list-search-field"
            placeholder="Search by name..."
            onChange={props.onUpdateFilter}
            value={props.filter}
            maxLength={200}
            type="search"
            size="large"
            label={"Search by name..."}
            labelHidden
          />
          <div className="search-icon">
            {!props.filter.length ? <SearchIconOverLay/> : ""}
          </div>
        </div>
        <div className={"toolbar__item"}>
          <SelectDropDown
            label="Select Sort"
            labelHidden
            size="large"
            onChange={props.onUpdateSort}
            value={props.sort}
          >
            <option value={SORT_ATTRIBUTE.lastEdited.key}>
              {SORT_ATTRIBUTE.lastEdited.displayVal}
            </option>
            <option value={SORT_ATTRIBUTE.a_z.key}>
              {SORT_ATTRIBUTE.a_z.displayVal}
            </option>
            <option value={SORT_ATTRIBUTE.z_a.key}>
              {SORT_ATTRIBUTE.z_a.displayVal}
            </option>
          </SelectDropDown>
        </div>
        {/* FLOE-6822  */}
        <div className={"toolbar__item toolbar__item--right"}>
          <CustomButton
            id={props.filter.length > 0 ? "add-dialog-close-disable" : "add-dialog-close"}
            fullWidth
            size="large"
            onClick={handleClick}
            ref={props.addButtonRefCb}
            disabledStyle={props.filter.length > 0}
          >
            Add
          </CustomButton>
          {showTooltip && (
            <div className="tooltipMessage">
              {props.hidden
                ? ""
                : "Disabled until filter is removed"}
            </div>
          )}
        </div>
        {/* FLOE-6822  */}
      </div>
      <div className="empty-product-msg">
        {props.emptyFilterState ? "No results found" : ""}
      </div>
    </div>
  );
};

export default ToolbarUtility;
